// web/src/@shared/store/useAuthStore.js OR src/store/useAuthStore.js
import { useEffect } from "react";
import { create } from "zustand";
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  createUserWithEmailAndPassword,
  linkWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { auth, db, storage } from "../firebase/config";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  writeBatch,
  serverTimestamp,
  updateDoc,
  increment,
  arrayUnion,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getCardImageByName } from "../utils/Utils";

const authDomainInflight = new Map();

const createInitialDomainFlags = () => ({
  cards: false,
  nftCards: false,
  decks: false,
  rewards: false,
  quests: false,
  questLog: false,
  purchases: false,
  cardSlapAnimations: false,
  tradeAnimations: false,
  friends: false,
  mail: false,
});

const mergeUserDataSections = (set, partial) => {
  set((prev) => ({
    userData: {
      ...(prev.userData || {}),
      ...partial,
    },
  }));
};

const setDomainLoadingState = (set, key, value) => {
  set((state) => ({
    loadingStates: {
      ...state.loadingStates,
      [key]: value,
    },
  }));
};

const setDomainLoadedState = (set, key, value = true) => {
  set((state) => ({
    loadedStates: {
      ...state.loadedStates,
      [key]: value,
    },
  }));
};

const readCompatQueryCachedFirst = async (queryRef, onCached) => {
  try {
    const cachedSnap = await queryRef.get({ source: "cache" });
    if (!cachedSnap.empty) {
      onCached?.(cachedSnap.docs);
    }
  } catch (_error) {
    // Cache miss is fine; we'll continue with the standard read.
  }

  const snap = await queryRef.get();
  return snap.docs;
};

const runDeferredUserLoad = async ({
  set,
  userId,
  domainKey,
  queryRef,
  mapDocs,
  assign,
  force = false,
}) => {
  const inflightKey = `${userId}:${domainKey}`;
  if (!force && authDomainInflight.has(inflightKey)) {
    return authDomainInflight.get(inflightKey);
  }

  const promise = (async () => {
    setDomainLoadingState(set, domainKey, true);

    try {
      const docs = await readCompatQueryCachedFirst(queryRef, (cachedDocs) => {
        assign(mapDocs(cachedDocs));
        setDomainLoadedState(set, domainKey);
      });

      const nextValue = mapDocs(docs);
      assign(nextValue);
      setDomainLoadedState(set, domainKey);
      return nextValue;
    } finally {
      setDomainLoadingState(set, domainKey, false);
    }
  })();

  authDomainInflight.set(inflightKey, promise);
  promise.finally(() => authDomainInflight.delete(inflightKey));
  return promise;
};

const subscribeToUserDomain = ({
  set,
  userId,
  domainKey,
  queryRef,
  mapDocs,
  assign,
}) => {
  setDomainLoadingState(set, domainKey, true);

  const unsubscribe = queryRef.onSnapshot(
    (snap) => {
      const nextValue = mapDocs(snap.docs);
      assign(nextValue);
      setDomainLoadedState(set, domainKey);
      setDomainLoadingState(set, domainKey, false);
    },
    (error) => {
      console.error(`Failed to subscribe to ${domainKey}:`, error);
      setDomainLoadingState(set, domainKey, false);
    },
  );

  return unsubscribe;
};

export const uploadImageAndGetUrl = async ({ file, path }) => {
  if (!file) return null;
  const r = ref(storage, path);
  await uploadBytes(r, file);
  return await getDownloadURL(r);
};

export const useAuthStore = create((set) => ({
  user: null,
  userData: null,
  loading: true,
  quests: [],
  questLog: [],
  loadingStates: createInitialDomainFlags(),
  loadedStates: createInitialDomainFlags(),
  setUserData: (updater) =>
    set((state) => ({
      userData:
        typeof updater === "function" ? updater(state.userData || {}) : updater,
    })),

  initAuth: () => {
    let unsubUser,
      unsubCards,
      unsubCharacters,
      unsubDecks,
      unsubRewards,
      unsubQuests,
      unsubquestLog,
      unsubPurchases,
      unsubCardSlapAnimations,
      unsubTradeAnimations,
      unsubFriends,
      unsubMail,
      unsubOpenedChests;

    onAuthStateChanged(auth, (firebaseUser) => {
      if (unsubUser) unsubUser();
      if (unsubCards) unsubCards();
      if (unsubCharacters) unsubCharacters();
      if (unsubDecks) unsubDecks();
      if (unsubRewards) unsubRewards();
      if (unsubQuests) unsubQuests();
      if (unsubquestLog) unsubquestLog();
      if (unsubPurchases) unsubPurchases();
      if (unsubCardSlapAnimations) unsubCardSlapAnimations();
      if (unsubTradeAnimations) unsubTradeAnimations();
      if (unsubFriends) unsubFriends();
      if (unsubMail) unsubMail();
      if (unsubOpenedChests) unsubOpenedChests();

      if (firebaseUser) {
        set({
          user: firebaseUser,
          userData: null,
          loading: false,
          quests: [],
          questLog: [],
          loadingStates: createInitialDomainFlags(),
          loadedStates: createInitialDomainFlags(),
        });

        const userRef = doc(db, "users", firebaseUser.uid);
        const openedChestsRef = collection(
          db,
          "users",
          firebaseUser.uid,
          "openedChests"
        );

        unsubUser = onSnapshot(userRef, (snap) => {
          if (!snap.exists()) return set({ userData: null });
          const userBaseData = { ...snap.data(), id: snap.id };

          (async () => {
            try {
              const referredBy = userBaseData?.referredBy;
              const level = Number(userBaseData?.level || 0);
              const alreadyUnlocked = !!userBaseData?.referralUnlockedAt;

              if (!referredBy || alreadyUnlocked) return;
              if (level < 5) return;

              const referralDocRef = doc(
                db,
                "users",
                referredBy,
                "referrals",
                firebaseUser.uid,
              );
              const referralSnap = await getDoc(referralDocRef);

              if (referralSnap.exists()) {
                await updateDoc(referralDocRef, {
                  claimable: true,
                  claimableAt: Date.now(),
                });
              }

              await updateDoc(userRef, {
                referralUnlockedAt: serverTimestamp(),
              });
            } catch (e) {
              console.error("Referral unlock check failed:", e);
            }
          })();

          mergeUserDataSections(set, userBaseData);
        });

        unsubOpenedChests = onSnapshot(openedChestsRef, (snap) => {
          const openedChests = snap.docs.reduce((acc, chestDoc) => {
            const data = chestDoc.data();
            const zone = data.zone;
            const chestId = data.chestId;
            if (!zone || !chestId) return acc;
            if (!acc[zone]) acc[zone] = {};
            acc[zone][chestId] = {
              id: chestDoc.id,
              ...data,
            };
            return acc;
          }, {});

          set((prev) => ({
            userData: { ...prev.userData, openedChests },
          }));
        });
      } else {
        set({
          user: null,
          userData: null,
          loading: false,
          quests: [],
          questLog: [],
          loadingStates: createInitialDomainFlags(),
          loadedStates: createInitialDomainFlags(),
        });
      }
    });
  },

  loadUserCards: async ({ force = false } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return [];

    return runDeferredUserLoad({
      set,
      userId,
      domainKey: "cards",
      queryRef: db.collection("users").doc(userId).collection("Infinity cards"),
      mapDocs: (docs) =>
        docs.map((cardDoc) => {
          const cardData = cardDoc.data();

          return {
            id: cardDoc.id,
            ...cardData,
            imageUrl:
              getCardImageByName(cardData?.name) || cardData?.imageUrl || "",
          };
        }),
      assign: (cards) => mergeUserDataSections(set, { cards }),
      force,
    });
  },

  loadUserCharacterCards: async ({ force = false } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return [];

    return runDeferredUserLoad({
      set,
      userId,
      domainKey: "nftCards",
      queryRef: db.collection("users").doc(userId).collection("Character cards"),
      mapDocs: (docs) =>
        docs.map((cardDoc) => ({
          id: cardDoc.id,
          ...cardDoc.data(),
        })),
      assign: (nftCards) => mergeUserDataSections(set, { nftCards }),
      force,
    });
  },

  loadUserDecks: async ({ force = false } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return [];

    return runDeferredUserLoad({
      set,
      userId,
      domainKey: "decks",
      queryRef: db.collection("users").doc(userId).collection("decks"),
      mapDocs: (docs) =>
        docs.map((deckDoc) => ({
          id: deckDoc.id,
          ...deckDoc.data(),
        })),
      assign: (decks) => mergeUserDataSections(set, { decks }),
      force,
    });
  },

  loadRewards: async ({ force = false } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return [];

    return runDeferredUserLoad({
      set,
      userId,
      domainKey: "rewards",
      queryRef: db.collection("users").doc(userId).collection("rewards"),
      mapDocs: (docs) =>
        docs.map((rewardDoc) => ({
          id: rewardDoc.id,
          ...rewardDoc.data(),
        })),
      assign: (rewards) => mergeUserDataSections(set, { rewards }),
      force,
    });
  },

  loadQuestData: async ({ force = false } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return { quests: [], questLog: [] };

    const inflightKey = `${userId}:questData`;
    if (!force && authDomainInflight.has(inflightKey)) {
      return authDomainInflight.get(inflightKey);
    }

    const promise = (async () => {
      setDomainLoadingState(set, "quests", true);
      setDomainLoadingState(set, "questLog", true);

      try {
        const questsRef = db.collection("users").doc(userId).collection("quests");
        const questLogRef = db.collection("users").doc(userId).collection("questLog");

        const [questDocs, questLogDocs] = await Promise.all([
          readCompatQueryCachedFirst(questsRef, (cachedDocs) => {
            const cachedQuests = cachedDocs.map((questDoc) => ({
              id: questDoc.id,
              ...questDoc.data(),
            }));
            mergeUserDataSections(set, { quests: cachedQuests });
            set({ quests: cachedQuests });
            setDomainLoadedState(set, "quests");
          }),
          readCompatQueryCachedFirst(questLogRef, (cachedDocs) => {
            const cachedQuestLog = cachedDocs.map((questDoc) => ({
              id: questDoc.id,
              ...questDoc.data(),
            }));
            mergeUserDataSections(set, { questLog: cachedQuestLog });
            set({ questLog: cachedQuestLog });
            setDomainLoadedState(set, "questLog");
          }),
        ]);

        const quests = questDocs.map((questDoc) => ({
          id: questDoc.id,
          ...questDoc.data(),
        }));
        const questLog = questLogDocs.map((questDoc) => ({
          id: questDoc.id,
          ...questDoc.data(),
        }));

        mergeUserDataSections(set, { quests, questLog });
        set({ quests, questLog });
        setDomainLoadedState(set, "quests");
        setDomainLoadedState(set, "questLog");
        return { quests, questLog };
      } finally {
        setDomainLoadingState(set, "quests", false);
        setDomainLoadingState(set, "questLog", false);
      }
    })();

    authDomainInflight.set(inflightKey, promise);
    promise.finally(() => authDomainInflight.delete(inflightKey));
    return promise;
  },

  loadPurchases: async ({ force = false } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return [];

    return runDeferredUserLoad({
      set,
      userId,
      domainKey: "purchases",
      queryRef: db.collection("users").doc(userId).collection("purchases"),
      mapDocs: (docs) =>
        docs.map((purchaseDoc) => ({
          id: purchaseDoc.id,
          ...purchaseDoc.data(),
        })),
      assign: (purchases) => mergeUserDataSections(set, { purchases }),
      force,
    });
  },

  loadFriends: async ({ force = false } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return [];

    return runDeferredUserLoad({
      set,
      userId,
      domainKey: "friends",
      queryRef: db.collection("users").doc(userId).collection("friends"),
      mapDocs: (docs) =>
        docs.map((friendDoc) => ({
          id: friendDoc.id,
          ...friendDoc.data(),
        })),
      assign: (friends) => mergeUserDataSections(set, { friends }),
      force,
    });
  },

  loadMail: async ({ force = false } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return [];

    return runDeferredUserLoad({
      set,
      userId,
      domainKey: "mail",
      queryRef: db.collection("users").doc(userId).collection("mail"),
      mapDocs: (docs) =>
        docs.map((mailDoc) => ({
          id: mailDoc.id,
          ...mailDoc.data(),
        })),
      assign: (mail) => mergeUserDataSections(set, { mail }),
      force,
    });
  },

  subscribeToRewards: () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return () => {};

    return subscribeToUserDomain({
      set,
      userId,
      domainKey: "rewards",
      queryRef: db.collection("users").doc(userId).collection("rewards"),
      mapDocs: (docs) =>
        docs.map((rewardDoc) => ({
          id: rewardDoc.id,
          ...rewardDoc.data(),
        })),
      assign: (rewards) => mergeUserDataSections(set, { rewards }),
    });
  },

  subscribeToUserCards: () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return () => {};

    return subscribeToUserDomain({
      set,
      userId,
      domainKey: "cards",
      queryRef: db.collection("users").doc(userId).collection("Infinity cards"),
      mapDocs: (docs) =>
        docs.map((cardDoc) => {
          const cardData = cardDoc.data();

          return {
            id: cardDoc.id,
            ...cardData,
            imageUrl:
              getCardImageByName(cardData?.name) || cardData?.imageUrl || "",
          };
        }),
      assign: (cards) => mergeUserDataSections(set, { cards }),
    });
  },

  subscribeToUserCharacterCards: () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return () => {};

    return subscribeToUserDomain({
      set,
      userId,
      domainKey: "nftCards",
      queryRef: db.collection("users").doc(userId).collection("Character cards"),
      mapDocs: (docs) =>
        docs.map((cardDoc) => ({
          id: cardDoc.id,
          ...cardDoc.data(),
        })),
      assign: (nftCards) => mergeUserDataSections(set, { nftCards }),
    });
  },

  subscribeToDecks: () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return () => {};

    return subscribeToUserDomain({
      set,
      userId,
      domainKey: "decks",
      queryRef: db.collection("users").doc(userId).collection("decks"),
      mapDocs: (docs) =>
        docs.map((deckDoc) => ({
          id: deckDoc.id,
          ...deckDoc.data(),
        })),
      assign: (decks) => mergeUserDataSections(set, { decks }),
    });
  },

  subscribeToPurchases: () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return () => {};

    return subscribeToUserDomain({
      set,
      userId,
      domainKey: "purchases",
      queryRef: db.collection("users").doc(userId).collection("purchases"),
      mapDocs: (docs) =>
        docs.map((purchaseDoc) => ({
          id: purchaseDoc.id,
          ...purchaseDoc.data(),
        })),
      assign: (purchases) => mergeUserDataSections(set, { purchases }),
    });
  },

  subscribeToFriends: () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return () => {};

    return subscribeToUserDomain({
      set,
      userId,
      domainKey: "friends",
      queryRef: db.collection("users").doc(userId).collection("friends"),
      mapDocs: (docs) =>
        docs.map((friendDoc) => ({
          id: friendDoc.id,
          ...friendDoc.data(),
        })),
      assign: (friends) => mergeUserDataSections(set, { friends }),
    });
  },

  subscribeToMail: () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return () => {};

    return subscribeToUserDomain({
      set,
      userId,
      domainKey: "mail",
      queryRef: db.collection("users").doc(userId).collection("mail"),
      mapDocs: (docs) =>
        docs.map((mailDoc) => ({
          id: mailDoc.id,
          ...mailDoc.data(),
        })),
      assign: (mail) => mergeUserDataSections(set, { mail }),
    });
  },

  loadAnimationInventory: async ({ force = false } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return { cardSlapAnimations: [], tradeAnimations: [] };
    }

    const inflightKey = `${userId}:animations`;
    if (!force && authDomainInflight.has(inflightKey)) {
      return authDomainInflight.get(inflightKey);
    }

    const promise = (async () => {
      setDomainLoadingState(set, "cardSlapAnimations", true);
      setDomainLoadingState(set, "tradeAnimations", true);

      try {
        const cardSlapRef = db
          .collection("users")
          .doc(userId)
          .collection("cardSlapAnimations");
        const tradeRef = db
          .collection("users")
          .doc(userId)
          .collection("tradeAnimations");

        const [cardSlapDocs, tradeDocs] = await Promise.all([
          readCompatQueryCachedFirst(cardSlapRef, (cachedDocs) => {
            const cachedCardSlaps = cachedDocs.map((animationDoc) => ({
              id: animationDoc.id,
              ...animationDoc.data(),
            }));
            mergeUserDataSections(set, {
              cardSlapAnimations: cachedCardSlaps,
            });
            setDomainLoadedState(set, "cardSlapAnimations");
          }),
          readCompatQueryCachedFirst(tradeRef, (cachedDocs) => {
            const cachedTradeAnimations = cachedDocs.map((animationDoc) => ({
              id: animationDoc.id,
              ...animationDoc.data(),
            }));
            mergeUserDataSections(set, {
              tradeAnimations: cachedTradeAnimations,
            });
            setDomainLoadedState(set, "tradeAnimations");
          }),
        ]);

        const cardSlapAnimations = cardSlapDocs.map((animationDoc) => ({
          id: animationDoc.id,
          ...animationDoc.data(),
        }));
        const tradeAnimations = tradeDocs.map((animationDoc) => ({
          id: animationDoc.id,
          ...animationDoc.data(),
        }));

        mergeUserDataSections(set, {
          cardSlapAnimations,
          tradeAnimations,
        });
        setDomainLoadedState(set, "cardSlapAnimations");
        setDomainLoadedState(set, "tradeAnimations");
        return { cardSlapAnimations, tradeAnimations };
      } finally {
        setDomainLoadingState(set, "cardSlapAnimations", false);
        setDomainLoadingState(set, "tradeAnimations", false);
      }
    })();

    authDomainInflight.set(inflightKey, promise);
    promise.finally(() => authDomainInflight.delete(inflightKey));
    return promise;
  },

  login: async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true, message: "Login successful" };
    } catch (error) {
      console.error("Login Error:", error.message);
      return { success: false, message: error.message };
    }
  },

  signInWithCustomToken: async (token) => {
    if (!token) {
      throw new Error("custom_token_missing");
    }

    await signInWithCustomToken(auth, token);
  },

  signUp: async (
    username,
    email,
    password,
    onboardingData = false,
    setGameScreenLoading,
  ) => {
    try {
      const emailLower = email.toLowerCase().trim();
      const pass = password.trim();

      const isAnon = auth.currentUser && auth.currentUser.isAnonymous;
      if (isAnon) {
        const credential = EmailAuthProvider.credential(emailLower, pass);
        await linkWithCredential(auth.currentUser, credential);
      } else {
        await createUserWithEmailAndPassword(auth, emailLower, pass);
      }

      const user = auth.currentUser;
      if (
        user &&
        !onboardingData &&
        typeof setGameScreenLoading === "function"
      ) {
        setGameScreenLoading(true);
      }

      const userRef = doc(db, "users", user.uid);
      const roles = ["player"];
      if (onboardingData?.role) roles.push(onboardingData.role);

      const referrerId = (localStorage.getItem("referrerId") || "").trim();
      const isSelfReferral = referrerId && referrerId === user.uid;

      const baseUserDoc = {
        username: username.toLowerCase().trim(),
        name: onboardingData ? onboardingData?.name : "",
        email: emailLower,
        emailVerified: false,
        onboardingComplete: false,
        roles,
        record: { wins: 0, losses: 0, ties: 0, dnfs: 0 },
        imageUrl: "",
        world: "Void City",
        avatar: {
          image:
            "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2FinfintyOrb%2Fio-green.png?alt=media&token=feff4088-f1da-43f7-9c7c-c231fe45b240",
          name: "Infinty Orb",
          ws: "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2Fgreen.png?alt=media&token=b504236c-a42f-4a2c-a2bd-9742b489a398",
        },
        xp: 0,
        level: 1,
        completedTutorial: false,
        selectedCardSlap: 1,
        selectedTradeAnimation: 1,
        lastLogin: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
        referredBy: referrerId && !isSelfReferral ? referrerId : null,
      };

      if (onboardingData?.role === "collection_creator") {
        baseUserDoc.collectionName = onboardingData.collectionName || "";
        baseUserDoc.xHandle = onboardingData.xHandle || "";
      }

      await setDoc(userRef, baseUserDoc, { merge: true });

      try {
        if (referrerId && !isSelfReferral) {
          const referrerUserRef = doc(db, "users", referrerId);
          const referrerSnap = await getDoc(referrerUserRef);
          if (referrerSnap.exists()) {
            const referralDocRef = doc(
              db,
              "users",
              referrerId,
              "referrals",
              user.uid,
            );

            const existingReferralSnap = await getDoc(referralDocRef);
            if (!existingReferralSnap.exists()) {
              await setDoc(referralDocRef, {
                id: user.uid,
                username: username.toLowerCase().trim(),
                claimable: false,
                claimed: false,
                createdAt: Date.now(),
                claimableAt: null,
                claimedAt: null,
                rewardKey: null,
                rewardPreview: null,
              });
            }
          }
        }
      } catch (e) {
        console.error("Failed to create referral doc:", e);
      }

      if (onboardingData?.role === "collection_creator") {
        const creatorId = user.uid;
        const collectionName = (onboardingData.collectionName || "").trim();
        const xHandle = (onboardingData.xHandle || "").trim();

        const safeSlug =
          collectionName
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "") || creatorId;

        const logoUrl = await uploadImageAndGetUrl({
          file: onboardingData.logoFile,
          path: `collections_onboarding/${creatorId}/logo_${Date.now()}.png`,
        });

        const bannerUrl = onboardingData.bannerFile
          ? await uploadImageAndGetUrl({
              file: onboardingData.bannerFile,
              path: `collections_onboarding/${creatorId}/banner_${Date.now()}.png`,
            })
          : null;

        const discordUrl = onboardingData?.discord || null;
        const mintPageUrl = onboardingData?.mintPage || null;

        const colRef = doc(db, "collections_onboarding", creatorId);
        await setDoc(
          colRef,
          {
            name: collectionName,
            slug: safeSlug,
            creatorId,
            xHandle: xHandle || null,
            logoUrl: logoUrl || "",
            bannerUrl: bannerUrl || null,
            discord: discordUrl,
            mintPage: mintPageUrl,
            totalPoints: 0,
            unlockedSlots: 0,
            supporters: [],
            approved: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        await setDoc(
          userRef,
          {
            collection: {
              id: creatorId,
              name: collectionName,
              slug: safeSlug,
              logoUrl: logoUrl || "",
              bannerUrl: bannerUrl || null,
            },
            updatedAt: new Date(),
          },
          { merge: true },
        );
      }

      try {
        const refCollectionId = localStorage.getItem("refCollectionId");
        if (refCollectionId) {
          const colRef = doc(db, "collections_onboarding", refCollectionId);
          await updateDoc(colRef, {
            totalPoints: increment(50),
            supporters: arrayUnion(user.uid),
            updatedAt: serverTimestamp(),
          });

          await setDoc(
            userRef,
            {
              referralCollectionId: refCollectionId,
              supportedCollections: arrayUnion(refCollectionId),
              referral: {
                collectionId: refCollectionId,
                appliedAt: serverTimestamp(),
                source: "referral_signup",
                points: 25,
              },
              updatedAt: new Date(),
            },
            { merge: true },
          );
        }
      } catch (e) {
        console.error("Collection referral award failed:", e);
      }

      if (referrerId && !isSelfReferral) {
        const referrerSnap = await getDoc(doc(db, "users", referrerId));
        if (referrerSnap.exists()) {
          const batch = writeBatch(db);
          const now = serverTimestamp();

          batch.set(doc(db, "users", user.uid, "friends", referrerId), {
            uid: referrerId,
            username: referrerSnap.data()?.username || "",
            referred: false,
            createdAt: now,
            status: "friend",
          });

          batch.set(doc(db, "users", referrerId, "friends", user.uid), {
            uid: user.uid,
            username: username.toLowerCase().trim(),
            referred: true,
            createdAt: now,
            status: "friend",
          });

          await batch.commit();
        }
      }

      localStorage.removeItem("referrerId");
      localStorage.removeItem("refCollectionId");
    } catch (error) {
      console.error("Sign Up Error:", error?.code, error?.message);
      throw error;
    }
  },

  logout: async () => {
    await signOut(auth);
    set({
      user: null,
      userData: null,
      quests: [],
      questLog: [],
      loadingStates: createInitialDomainFlags(),
      loadedStates: createInitialDomainFlags(),
    });
  },
}));

export const useEnsureUserData = ({
  enabled = true,
  cards = false,
  characterCards = false,
  decks = false,
  rewards = false,
  questData = false,
  purchases = false,
  friends = false,
  mail = false,
  animations = false,
  force = false,
} = {}) => {
  const userId = useAuthStore((state) => state.user?.uid || state.userData?.id);
  const loadingStates = useAuthStore((state) => state.loadingStates);
  const loadedStates = useAuthStore((state) => state.loadedStates);
  const loadUserCards = useAuthStore((state) => state.loadUserCards);
  const loadUserCharacterCards = useAuthStore(
    (state) => state.loadUserCharacterCards,
  );
  const loadUserDecks = useAuthStore((state) => state.loadUserDecks);
  const loadRewards = useAuthStore((state) => state.loadRewards);
  const loadQuestData = useAuthStore((state) => state.loadQuestData);
  const loadPurchases = useAuthStore((state) => state.loadPurchases);
  const loadFriends = useAuthStore((state) => state.loadFriends);
  const loadMail = useAuthStore((state) => state.loadMail);
  const loadAnimationInventory = useAuthStore(
    (state) => state.loadAnimationInventory,
  );

  useEffect(() => {
    if (!enabled || !userId) return;

    if (cards && (force || (!loadedStates.cards && !loadingStates.cards))) {
      loadUserCards({ force }).catch((error) => {
        console.error("Failed to load user cards:", error);
      });
    }

    if (
      characterCards &&
      (force || (!loadedStates.nftCards && !loadingStates.nftCards))
    ) {
      loadUserCharacterCards({ force }).catch((error) => {
        console.error("Failed to load user character cards:", error);
      });
    }

    if (decks && (force || (!loadedStates.decks && !loadingStates.decks))) {
      loadUserDecks({ force }).catch((error) => {
        console.error("Failed to load user decks:", error);
      });
    }

    if (rewards && (force || (!loadedStates.rewards && !loadingStates.rewards))) {
      loadRewards({ force }).catch((error) => {
        console.error("Failed to load rewards:", error);
      });
    }

    if (
      questData &&
      (force ||
        ((!loadedStates.quests && !loadingStates.quests) ||
          (!loadedStates.questLog && !loadingStates.questLog)))
    ) {
      loadQuestData({ force }).catch((error) => {
        console.error("Failed to load quest data:", error);
      });
    }

    if (
      purchases &&
      (force || (!loadedStates.purchases && !loadingStates.purchases))
    ) {
      loadPurchases({ force }).catch((error) => {
        console.error("Failed to load purchases:", error);
      });
    }

    if (friends && (force || (!loadedStates.friends && !loadingStates.friends))) {
      loadFriends({ force }).catch((error) => {
        console.error("Failed to load friends:", error);
      });
    }

    if (mail && (force || (!loadedStates.mail && !loadingStates.mail))) {
      loadMail({ force }).catch((error) => {
        console.error("Failed to load mail:", error);
      });
    }

    if (
      animations &&
      (force ||
        ((!loadedStates.cardSlapAnimations &&
          !loadingStates.cardSlapAnimations) ||
          (!loadedStates.tradeAnimations && !loadingStates.tradeAnimations)))
    ) {
      loadAnimationInventory({ force }).catch((error) => {
        console.error("Failed to load animation inventory:", error);
      });
    }
  }, [
    enabled,
    userId,
    cards,
    characterCards,
    decks,
    rewards,
    questData,
    purchases,
    friends,
    mail,
    animations,
    force,
    loadingStates.cards,
    loadingStates.nftCards,
    loadingStates.decks,
    loadingStates.rewards,
    loadingStates.quests,
    loadingStates.questLog,
    loadingStates.purchases,
    loadingStates.friends,
    loadingStates.mail,
    loadingStates.cardSlapAnimations,
    loadingStates.tradeAnimations,
    loadedStates.cards,
    loadedStates.nftCards,
    loadedStates.decks,
    loadedStates.rewards,
    loadedStates.quests,
    loadedStates.questLog,
    loadedStates.purchases,
    loadedStates.friends,
    loadedStates.mail,
    loadedStates.cardSlapAnimations,
    loadedStates.tradeAnimations,
    loadUserCards,
    loadUserCharacterCards,
    loadUserDecks,
    loadRewards,
    loadQuestData,
    loadPurchases,
    loadFriends,
    loadMail,
    loadAnimationInventory,
  ]);
};

export const useLiveUserData = ({
  enabled = true,
  cards = false,
  characterCards = false,
  decks = false,
  rewards = false,
  purchases = false,
  friends = false,
  mail = false,
} = {}) => {
  const userId = useAuthStore((state) => state.user?.uid || state.userData?.id);
  const subscribeToUserCards = useAuthStore((state) => state.subscribeToUserCards);
  const subscribeToUserCharacterCards = useAuthStore(
    (state) => state.subscribeToUserCharacterCards,
  );
  const subscribeToDecks = useAuthStore((state) => state.subscribeToDecks);
  const subscribeToRewards = useAuthStore((state) => state.subscribeToRewards);
  const subscribeToPurchases = useAuthStore(
    (state) => state.subscribeToPurchases,
  );
  const subscribeToFriends = useAuthStore((state) => state.subscribeToFriends);
  const subscribeToMail = useAuthStore((state) => state.subscribeToMail);

  useEffect(() => {
    if (!enabled || !userId) return undefined;

    const unsubscribers = [];

    if (cards) {
      unsubscribers.push(subscribeToUserCards());
    }

    if (characterCards) {
      unsubscribers.push(subscribeToUserCharacterCards());
    }

    if (decks) {
      unsubscribers.push(subscribeToDecks());
    }

    if (rewards) {
      unsubscribers.push(subscribeToRewards());
    }

    if (purchases) {
      unsubscribers.push(subscribeToPurchases());
    }

    if (friends) {
      unsubscribers.push(subscribeToFriends());
    }

    if (mail) {
      unsubscribers.push(subscribeToMail());
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === "function") {
          unsubscribe();
        }
      });
    };
  }, [
    enabled,
    userId,
    cards,
    characterCards,
    decks,
    rewards,
    purchases,
    friends,
    mail,
    subscribeToUserCards,
    subscribeToUserCharacterCards,
    subscribeToDecks,
    subscribeToRewards,
    subscribeToPurchases,
    subscribeToFriends,
    subscribeToMail,
  ]);
};
